import React, { useEffect, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import accountIcon from '@/assets/images/header-menu/account-icon.svg';
import signOutIcon from '@/assets/images/header-menu/sign-out-icon.svg';
import './MaHeaderUserMenu.css';
import type { MaHeaderUserInfo } from '@/utils/maHeaderUser';

export type MaHeaderUserMenuProps = {
  userInfo: MaHeaderUserInfo | null | undefined;
  userInitials: string;
  userDisplayName: string;
  onMyProfile: () => void;
  onSignOut: () => void | Promise<void>;
  /** Optional — defaults match legacy SA ProfileDropdown (new tab). */
  termsHref?: string;
  privacyHref?: string;
};

/**
 * Same structure and styles as krosskast-v2-master-account-ui MaHeaderUserMenu.js
 */
const MaHeaderUserMenu: React.FC<MaHeaderUserMenuProps> = ({
  userInfo,
  userInitials,
  userDisplayName,
  onMyProfile,
  onSignOut,
  termsHref = '/terms',
  privacyHref = '/privacy',
}) => {
  const [show, setShow] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);

  useEffect(() => {
    setProfileImageError(false);
  }, [userInfo?.avatar]);

  const emailLine = ((userInfo?.email || '').trim());

  return (
    <div className="ma-profile-dropdown-wrapper">
      <Dropdown show={show} onToggle={setShow} align="end">
        <Dropdown.Toggle
          as={Button}
          variant="link"
          className="ma-profile-avatar-toggle"
          aria-label="Open profile menu"
        >
          <div className="ma-profile-avatar" aria-hidden="true">
            {userInfo?.avatar && !profileImageError ? (
              <img src={userInfo.avatar} alt="" onError={() => setProfileImageError(true)} />
            ) : (
              <span className="ma-profile-avatar__initials">{userInitials}</span>
            )}
          </div>
          <span className="user-menu-display-name">{userDisplayName}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="kk-user-dropdown"
          popperConfig={{ strategy: 'fixed' }}
        >
          <div className="kk-user-dropdown__header">
            <div className="kk-user-dropdown__profile-container">
              <div className="kk-user-dropdown__avatar">
                {userInfo?.avatar && !profileImageError ? (
                  <img
                    src={userInfo.avatar}
                    alt=""
                    className="kk-user-dropdown__profile-image"
                    onError={() => setProfileImageError(true)}
                  />
                ) : (
                  <div className="kk-user-dropdown__default-avatar kk-user-dropdown__default-avatar--initials">
                    <span className="kk-user-dropdown__avatar-initials-text">{userInitials}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="kk-user-dropdown__info">
              <div className="kk-user-dropdown__greeting">
                Hi{' '}
                {(userInfo?.firstName || '').trim() ||
                  (userDisplayName && userDisplayName !== 'User'
                    ? userDisplayName.trim().split(/\s+/)[0]
                    : '') ||
                  'there'}
              </div>
              <div className="kk-user-dropdown__email">{emailLine}</div>
            </div>
          </div>
          <button
            type="button"
            className="kk-user-dropdown__link"
            onClick={() => {
              setShow(false);
              onMyProfile();
            }}
          >
            <img src={accountIcon} alt="" className="submenu-icon" />
            My Profile
          </button>
          <button
            type="button"
            className="kk-user-dropdown__sign-out"
            onClick={async () => {
              setShow(false);
              await onSignOut();
            }}
          >
            <img src={signOutIcon} alt="" className="sign-out-icon" />
            Sign Out
          </button>
          <div className="kk-user-dropdown__footer">
            <a href={termsHref} target="_blank" rel="noopener noreferrer" className="kk-user-dropdown__link">
              Terms and Conditions
            </a>
            <span>•</span>
            <a href={privacyHref} target="_blank" rel="noopener noreferrer" className="kk-user-dropdown__link">
              Privacy Policy
            </a>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default MaHeaderUserMenu;
